// InstallGate — First-Touch-Install-Screen für mobile Browser
// ===========================================================
//
// Wave 14ca-fix2: Reihenfolge final umgedreht. InstallGate ist jetzt der
// ALLERERSTE Screen, den ein neuer User auf einem mobilen Browser sieht
// — VOR Login. User-Wunsch:
//   "Diese Login-Seite soll erst erscheinen, wenn der Kunde die PWA auf
//    dem Desktop abgelegt hat, damit er das später nicht vergisst. Beim
//    ersten Mal muss also eine total knappe Einleitung stehen mit
//    Bildchen wie man die App auf iPhone/Android aufs Homescreen legt.
//    Danach (Standalone-PWA) kommt erst der Login (Code oder E-Mail)."
//
// Vorteile dieser Reihenfolge:
//   - User vergisst den Install-Schritt nicht (war im Friend-Test ein Problem)
//   - Login passiert IM PWA → keine iOS-Storage-Falle mehr (Safari ↔ PWA
//     teilen kein Storage; früher musste man sich nach Install nochmal
//     einloggen, das ist jetzt unnötig).
//   - Magic-Link-Mail aus iOS Mail öffnet zwar weiter in Safari, aber das
//     ist für die Erstanmeldung mit Code irrelevant — der Code wird in
//     der PWA selbst eingegeben. Returning Users mit Magic-Link auf
//     einem frischen Device sind ein Edge-Case.
//
// Skip-Bedingungen (ALLE müssen FALSE sein, sonst Gate wird gezeigt):
//   - bereits standalone (= als PWA installiert) → Login direkt zeigen
//   - Desktop-Browser → kein Touch-Install-Pattern, App geht im Browser
//
// Kein Bypass-Knopf: der Gate ist bewusst hart. Auf Mobile ohne Install
// soll die App nicht laufen, weil der User sonst nie auf den Homescreen-
// Tap kommt (er vergisst es). Wer trotzdem im Browser bleiben will, kann
// nichts machen außer installieren — das ist der Punkt.
//
// Für iOS-Chrome / iOS-Firefox: dort GEHT kein Add-to-Home-Screen — nur
// in Safari. Für die zeigen wir einen Hinweis "Open this URL in Safari".

import { useEffect, useState, type ReactNode } from 'react';

type Platform =
  | 'ios-safari'      // Add-to-Home-Screen über Share-Sheet
  | 'ios-other'       // iOS Chrome/Firefox/etc — Install nur in Safari möglich
  | 'ios-inapp'       // In-App-Browser (WhatsApp, Instagram, Mail) — kein Install
  | 'android-chrome'  // Install via ⋮-Menu oder beforeinstallprompt
  | 'desktop'         // wir skippen
  | 'unknown';

function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent;

  // iOS-Detection: iPad/iPhone/iPod direkt, plus iPadOS-Mimicking-Mac (ab
  // iPadOS 13: UA = "Macintosh" aber `maxTouchPoints > 1`).
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  if (isIOS) {
    // iOS-Browser-Subtyp: alles ausser Safari hat ein In-WebKit-Wrapper-
    // Kürzel, kann KEIN Add-to-Home-Screen → Hinweis "open in Safari".
    if (/CriOS|FxiOS|EdgiOS|OPiOS|GSA|YaBrowser/i.test(ua)) return 'ios-other';
    // In-App-Browser (WhatsApp, Instagram, Facebook, Mail, …) sind WKWebViews
    // ohne Safari-Chrome: sie können KEIN Add-to-Home-Screen, tragen aber
    // keinen der Marker oben. Erkennbar am fehlenden `Safari/`-Token — echtes
    // Safari sendet immer `Version/x … Safari/y`. Wichtig, weil Einladungen
    // typischerweise per WhatsApp ankommen und der Link dort im In-App-
    // Browser aufgeht (Beta-Tester-Fall 2026-08-25).
    if (/FBAN|FBAV|Instagram|Line\/|Snapchat|Twitter|MicroMessenger/i.test(ua)) {
      return 'ios-inapp';
    }
    if (!/Safari\//.test(ua)) return 'ios-inapp';
    return 'ios-safari';
  }
  if (/Android/i.test(ua)) return 'android-chrome';

  // Alles andere = Desktop (Mac ohne Touch, Windows, Linux).
  return 'desktop';
}

/** Aktueller Host für die Anleitungen — NIE hardcoden: das Repo ist ein Fork,
 *  und ein hartverdrahtetes `alldayturbo.com` schickt ULTRA-User in die
 *  falsche (MTB-)App, wo ihr Invite-Code und ihr Account nicht existieren.
 *  Genau das ist am 2026-08-25 einem Beta-Tester passiert. */
function currentHost(): string {
  if (typeof window === 'undefined') return 'ultra.alldayturbo.com';
  return window.location.host;
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  // iOS-spezifisch (nicht standardisiert): `navigator.standalone`.
  const iosStandalone =
    (window.navigator as unknown as { standalone?: boolean }).standalone === true;
  // Cross-browser (Android Chrome PWA, Desktop PWA): display-mode-Query.
  const displayStandalone = window.matchMedia('(display-mode: standalone)').matches;
  return iosStandalone || displayStandalone;
}

export function InstallGate({ children }: { children: ReactNode }) {
  // SSR-safe Initial-State: angenommen "alles passt" (rendert children),
  // entscheiden im useEffect nach Mount. Verhindert Hydration-Flicker.
  const [shouldShow, setShouldShow] = useState<boolean>(false);
  const [platform, setPlatform] = useState<Platform>('unknown');

  useEffect(() => {
    if (isStandalone()) return;          // schon installiert → skip
    const p = detectPlatform();
    if (p === 'desktop' || p === 'unknown') return; // nur mobile relevant
    setPlatform(p);
    setShouldShow(true);
  }, []);

  if (!shouldShow) return <>{children}</>;

  return (
    <div className="fixed inset-0 z-[110] bg-turbo-900 flex items-center justify-center p-4 overflow-auto">
      <div className="max-w-md w-full bg-turbo-700/40 border border-turbo-500/30 rounded-2xl p-6 shadow-2xl my-auto">
        <Header />

        <p className="text-sm text-white/80 leading-relaxed mb-5">
          Welcome — first add{' '}
          <span className="font-semibold text-white">All Day Turbo ULTRA</span> to
          your home screen.
        </p>

        {platform === 'ios-safari' && <IOSSafariSteps />}
        {platform === 'ios-other' && <IOSOtherBrowserHint />}
        {platform === 'ios-inapp' && <IOSInAppBrowserHint />}
        {platform === 'android-chrome' && <AndroidChromeSteps />}
      </div>
    </div>
  );
}

// ─── Header ───────────────────────────────────────────────────────────────

function Header() {
  return (
    <div className="flex items-center gap-3 mb-4">
      <img
        src="/icons/icon-192.png?v=14bk1"
        alt="All Day Turbo ULTRA"
        width={44}
        height={44}
        className="w-11 h-11 rounded-lg shrink-0"
      />
      <div>
        <div className="text-base font-semibold text-white">All Day Turbo ULTRA</div>
        <div className="text-xs text-white/55">Add to home screen</div>
      </div>
    </div>
  );
}

// ─── Platform-spezifische Anleitungen ─────────────────────────────────────

function IOSSafariSteps() {
  return (
    <ol className="space-y-3 text-sm text-white/85">
      <Step n={1}>
        Tap the <MoreIcon /> <em className="not-italic font-medium">menu</em>{' '}
        at the bottom right of Safari.
      </Step>
      <Step n={2}>
        Tap <ShareIcon /> <em className="not-italic font-medium">Share</em>,
        then scroll and tap{' '}
        <span className="font-medium">Add to Home Screen</span>.
      </Step>
      <Step n={3}>
        Tap <span className="font-medium">Add</span> in the top right.
      </Step>
      <Step n={4}>
        Open <span className="font-medium">All Day Turbo ULTRA</span> from your home
        screen and sign up there.
      </Step>
    </ol>
  );
}

function IOSOtherBrowserHint() {
  return (
    <div className="text-sm text-white/85 space-y-3">
      <div className="bg-amber-500/10 border border-amber-500/40 rounded-lg p-3">
        <div className="font-semibold text-amber-200 mb-1">
          Open in Safari to install
        </div>
        <p className="text-xs text-amber-100/85 leading-relaxed">
          On iPhone / iPad only Safari can install web apps to the home screen.
          Open <span className="font-mono">{currentHost()}</span> in Safari, tap
          the <MoreIcon /> menu → <ShareIcon /> Share →{' '}
          <em>Add to Home Screen</em>.
        </p>
      </div>
    </div>
  );
}

function IOSInAppBrowserHint() {
  return (
    <div className="text-sm text-white/85 space-y-3">
      <div className="bg-amber-500/10 border border-amber-500/40 rounded-lg p-3">
        <div className="font-semibold text-amber-200 mb-1">
          Open in Safari to install
        </div>
        <p className="text-xs text-amber-100/85 leading-relaxed">
          You opened this link inside another app (WhatsApp, Instagram, Mail …).
          Those in-app browsers cannot add apps to the home screen. Tap the{' '}
          <MoreIcon /> menu → <em>Open in Safari</em>, or open{' '}
          <span className="font-mono">{currentHost()}</span> in Safari directly.
        </p>
      </div>
    </div>
  );
}

function AndroidChromeSteps() {
  return (
    <ol className="space-y-3 text-sm text-white/85">
      <Step n={1}>
        Tap the <span className="font-mono text-base align-middle">⋮</span>{' '}
        menu (top right).
      </Step>
      <Step n={2}>
        Tap <span className="font-medium">Install app</span> (or{' '}
        <span className="font-medium">Add to Home screen</span>).
      </Step>
      <Step n={3}>
        Open <span className="font-medium">All Day Turbo ULTRA</span> from your home
        screen and sign up there.
      </Step>
    </ol>
  );
}

function Step({ n, children }: { n: number; children: ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <span className="shrink-0 w-6 h-6 rounded-full bg-orange-500/25 border border-orange-500/60 text-orange-200 text-xs font-semibold flex items-center justify-center mt-0.5">
        {n}
      </span>
      <div className="leading-relaxed flex-1">{children}</div>
    </li>
  );
}

/** iOS-Safari Share-Icon: hochgestelltes Quadrat mit Pfeil nach oben. */
function ShareIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="inline-block align-text-bottom -translate-y-px"
      aria-hidden="true"
    >
      <path d="M12 16V4" />
      <polyline points="7 9 12 4 17 9" />
      <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
    </svg>
  );
}

/** iOS-Safari More-Menu-Icon: drei horizontale Punkte. Im neuen Safari
 *  (iOS 17+) sitzt der Share-Button im More-Menü, nicht mehr direkt in
 *  der Bottom-Bar — daher dieses Icon im Schritt 1 der Anleitung. */
function MoreIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="currentColor"
      className="inline-block align-text-bottom -translate-y-px"
      aria-hidden="true"
    >
      <circle cx="5" cy="12" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="19" cy="12" r="2" />
    </svg>
  );
}
